<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisStaticMap from 'dashboard/components-next/LaisStaticMap.vue';
import VisitCardTagGroup from './VisitCardTagGroup.vue';

const props = defineProps({
  visit: { type: Object, default: () => ({}) },
  unsuccessful: { type: Boolean, default: false },
  offerLabel: { type: String, default: 'Encaminhar link da proposta' },
});

const emit = defineEmits([
  'message-resident',
  'message-owner',
  'offer',
  'call',
  'listing',
  'expand',
]);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const additionalAttributes = computed(
  () =>
    props.visit.additionalAttributes || props.visit.additional_attributes || {}
);
const property = computed(() => props.visit.imovel || {});
const contact = computed(() => props.visit.contact || {});
const owner = computed(
  () =>
    property.value.owner ||
    property.value.proprietario ||
    additionalAttributes.value.owner ||
    additionalAttributes.value.proprietario ||
    {}
);
const propertyTitle = computed(
  () =>
    property.value.titulo ||
    property.value.title ||
    property.value.endereco ||
    props.visit.address ||
    'Imóvel visitado'
);
const propertyAddress = computed(
  () =>
    firstPresent(
      property.value.endereco,
      property.value.address,
      props.visit.address,
      propertyTitle.value
    ) || 'Endereço não informado'
);
const listingHref = computed(
  () =>
    firstPresent(
      property.value.publicUrl,
      property.value.public_url,
      property.value.linkExterno,
      property.value.link_externo,
      property.value.url,
      additionalAttributes.value.listingUrl,
      additionalAttributes.value.listing_url
    ) || ''
);
const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});
const moneyLabel = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0
    ? moneyFormatter.format(numeric)
    : '';
};
const priceLabel = computed(
  () =>
    moneyLabel(
      firstPresent(
        property.value.valor,
        property.value.price,
        property.value.rentValue,
        property.value.rent_value,
        property.value.salePrice,
        property.value.sale_price
      )
    ) || ''
);
const scheduledAtLabel = computed(() => {
  const value = props.visit.scheduledAt || props.visit.scheduled_at;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
});
const problemLabel = computed(
  () =>
    firstPresent(
      props.visit.summary?.problemLabel,
      props.visit.summary?.reasonLabel,
      props.visit.summary?.cancellationReasonLabel,
      additionalAttributes.value.entrance_problem_label,
      additionalAttributes.value.entranceProblemLabel,
      additionalAttributes.value.reason_label,
      additionalAttributes.value.reasonLabel
    ) || ''
);
const details = computed(() =>
  [
    { label: 'Cliente', value: contact.value.name || props.visit.clientName },
    { label: 'Data da visita', value: scheduledAtLabel.value },
    { label: 'Valor do anúncio', value: priceLabel.value },
    { label: 'Proprietário', value: owner.value.name || owner.value.nome },
    {
      label: 'Observações',
      value: props.visit.observacoes || props.visit.notes,
    },
  ].filter(item => item.value)
);

const openListing = () => {
  if (!listingHref.value) return;
  emit('listing', listingHref.value);
  if (typeof window !== 'undefined') {
    window.open(listingHref.value, '_blank', 'noopener');
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard
    density="flush"
    expandable
    :start-expanded="!unsuccessful"
    expand-label="Mostrar detalhes"
    collapse-label="Esconder detalhes"
    @toggle="emit('expand', $event)"
  >
    <template #header>
      <div class="min-w-0">
        <h3 class="truncate text-[15px] font-semibold text-lais-text">
          {{ propertyTitle }}
        </h3>
        <p class="mt-1 truncate text-[12px] text-lais-text-muted">
          {{ propertyAddress }}
        </p>
      </div>
    </template>
    <LaisStaticMap
      :src="visit.imovel?.coverUrl || visit.imovel?.thumbnailUrl"
      :alt="visit.imovel?.titulo || 'Imóvel visitado'"
      aspect-class="aspect-[16/7]"
    />
    <div class="grid gap-4 p-4">
      <VisitCardTagGroup
        :status-label="
          unsuccessful ? 'Visita não realizada' : 'Visita realizada'
        "
        :status-variant="unsuccessful ? 'error' : 'success'"
        :tenant-living="Boolean(visit.tenantLiving)"
        :third-party="Boolean(visit.thirdParty)"
      />
      <div>
        <h3 class="text-[15px] font-semibold text-lais-text">
          {{ propertyTitle }}
        </h3>
        <p class="mt-1 text-[13px] text-lais-text-muted">
          {{ propertyAddress }}
        </p>
        <p
          v-if="problemLabel"
          class="mt-2 rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-900"
        >
          {{ problemLabel }}
        </p>
      </div>

      <dl v-if="details.length" class="grid gap-2 text-[13px] sm:grid-cols-2">
        <div
          v-for="item in details"
          :key="item.label"
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
        >
          <dt class="text-[11px] font-semibold uppercase text-lais-text-muted">
            {{ item.label }}
          </dt>
          <dd class="mt-1 font-medium text-lais-text-body">
            {{ item.value }}
          </dd>
        </div>
      </dl>

      <div class="grid gap-2 sm:grid-cols-2">
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text"
          @click="emit('message-resident')"
        >
          Conversar com o inquilino
        </button>
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text"
          @click="emit('message-owner')"
        >
          Conversar com proprietário
        </button>
        <button
          v-if="!unsuccessful"
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-primary"
          @click="emit('offer')"
        >
          {{ offerLabel }}
        </button>
        <button
          v-if="listingHref"
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text"
          @click="openListing"
        >
          Ver anúncio
        </button>
        <button
          type="button"
          class="rounded-lais border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-text"
          @click="emit('call')"
        >
          Ligar
        </button>
      </div>
    </div>
  </LaisCard>
</template>
